import {
  premiumReportPlanMap,
  type PremiumReportPlanId,
  type ReportDeliverables,
  type ReportPerk,
} from "@ishvaram/domain-data/report-plans";
import { PLAN_ID, type SEOReportPlanId } from "@/lib/seo-intents";

/**
 * The governed answer-continuation deliverables triplet for a plan (rule 40 —
 * the single source is Supabase catalog.report_deliverables → domain-data; never
 * re-authored in a .tsx). Falls back to the Brihat full-chart triplet for an
 * unknown id so a caller always has 3 rows to render.
 */
export function reportDeliverablesFor(planId: PremiumReportPlanId): ReportDeliverables {
  return (
    premiumReportPlanMap[planId]?.deliverables ??
    premiumReportPlanMap[PLAN_ID.brihat_kundali].deliverables
  );
}

/**
 * The "what you get" perk badges for a plan (Supabase catalog.perks +
 * catalog.product_perks → domain-data; never hand-authored in a .tsx). Returns
 * only DISPLAY-safe perks (`is_entitlement=false`) — entitlement grants
 * (credits, follow-ups) are the backend's job, not an offer-card badge. Empty
 * for a plan with no perks OR before the perks mirror is generated, so callers
 * render nothing rather than guessing.
 */
export function reportPerksFor(planId: PremiumReportPlanId): ReportPerk[] {
  const perks = premiumReportPlanMap[planId]?.perks ?? [];
  return perks.filter((perk) => !perk.is_entitlement);
}

export type ReportCopyLanguage = "en" | "hi";

export type ReportOfferCopy = {
  priceInr: number;
  headline: string;
  subhead: string;
};

export function normalizedReportCopyLanguage(language?: string | null): ReportCopyLanguage {
  return language === "hi" ? "hi" : "en";
}

export const REPORT_COPY: Record<SEOReportPlanId, ReportOfferCopy> = {
  [PLAN_ID.brihat_kundali]: {
    priceInr: premiumReportPlanMap.brihat_kundali.price_inr,
    headline: "See why this pattern keeps repeating",
    subhead:
      "A paid kundali report with the house, dasha, dosha, and upay behind your current phase.",
  },
  [PLAN_ID.marriage_report]: {
    priceInr: premiumReportPlanMap.marriage_report.price_inr,
    headline: "Why do relationships repeat the same pattern?",
    subhead:
      "Read the 7th house, Shukra, Mangal Dosh, compatibility pattern, timing, and upay in one focused report.",
  },
  [PLAN_ID.career_report]: {
    priceInr: premiumReportPlanMap.career_report.price_inr,
    headline: "Why does hard work not turn into results?",
    subhead:
      "See your 10th house, Shani pattern, active dasha, blocked potential, and better work windows.",
  },
  [PLAN_ID.annual_forecast]: {
    priceInr: premiumReportPlanMap.annual_forecast.price_inr,
    headline: "Why does this year feel different already?",
    subhead:
      "Map the next 12 months through your dasha, transits, caution periods, decision windows, and upay.",
  },
  [PLAN_ID.sade_sati]: {
    priceInr: premiumReportPlanMap.sade_sati.price_inr,
    headline: "Why is Shani making this phase feel heavy?",
    subhead:
      "Read your Sade Sati phase, natal Shani, dasha context, pressure areas, and safe Shani upay.",
  },
  [PLAN_ID.pitra_dosh]: {
    priceInr: premiumReportPlanMap.pitra_dosh.price_inr,
    headline: "Is ancestral karma affecting your path?",
    subhead:
      "Confirm Pitra Dosh, cancellation factors, activation windows, and the exact upay sequence for your chart.",
  },
  [PLAN_ID.mahadasha_forecast]: {
    priceInr: premiumReportPlanMap.mahadasha_forecast.price_inr,
    headline: "Which mahadasha is deciding your timing?",
    subhead: "Map your current mahadasha, antardasha, next transitions, and month-level decisions.",
  },
  [PLAN_ID.muhurat_report]: {
    priceInr: premiumReportPlanMap.muhurat_report.price_inr,
    headline: "Why does the right time depend on your chart?",
    subhead:
      "Get purpose-specific muhurat guidance, avoid windows, sankalp, and ritual checklist from your kundali.",
  },
  [PLAN_ID.dosha_remedy_plan]: {
    priceInr: premiumReportPlanMap.dosha_remedy_plan.price_inr,
    headline: "Why choose an upay before confirming the dosha?",
    subhead:
      "Confirm dosha strength, cancellation factors, safe upay, mantra routine, and matched remedy products.",
  },
};

const HINDI_REPORT_COPY: Record<SEOReportPlanId, ReportOfferCopy> = {
  [PLAN_ID.brihat_kundali]: {
    priceInr: REPORT_COPY.brihat_kundali.priceInr,
    headline: "Brihat Kundali Report me pattern samjhein",
    subhead:
      "12 house, mahadasha, dosha aur upay ko ek readable PDF me paayein, chart ke exact graha ke saath.",
  },
  [PLAN_ID.marriage_report]: {
    priceInr: REPORT_COPY.marriage_report.priceInr,
    headline: "Kundali Milan Report se rishta pattern samjhein",
    subhead:
      "7th house, Shukra, Mangal Dosh, Gun Milan, timing aur upay ko ek family-shareable PDF me paayein.",
  },
  [PLAN_ID.career_report]: {
    priceInr: REPORT_COPY.career_report.priceInr,
    headline: "Career Report me Shani pattern dekhein",
    subhead:
      "10th house, active dasha, blocked potential aur better work windows ko chart ke saath samjhein.",
  },
  [PLAN_ID.annual_forecast]: {
    priceInr: REPORT_COPY.annual_forecast.priceInr,
    headline: "Annual Forecast me 12-month timing dekhein",
    subhead:
      "Dasha, transit, caution periods, decision windows aur upay ko month-by-month PDF me paayein.",
  },
  [PLAN_ID.sade_sati]: {
    priceInr: REPORT_COPY.sade_sati.priceInr,
    headline: "Sade Sati Report me Shani pressure samjhein",
    subhead:
      "Moon sign phase, natal Shani, dasha context, pressure areas aur safe Shani upay ko chart se padhein.",
  },
  [PLAN_ID.pitra_dosh]: {
    priceInr: REPORT_COPY.pitra_dosh.priceInr,
    headline: "Pitra Dosh Report se ancestor pattern confirm karein",
    subhead:
      "9th house, Surya, Rahu/Ketu, cancellation, dasha activation aur safe ancestral upay ko ek PDF me paayein.",
  },
  [PLAN_ID.mahadasha_forecast]: {
    priceInr: REPORT_COPY.mahadasha_forecast.priceInr,
    headline: "Mahadasha Forecast me timing map karein",
    subhead:
      "Current mahadasha, antardasha, next transition aur month-level decisions ko chart se samjhein.",
  },
  [PLAN_ID.muhurat_report]: {
    priceInr: REPORT_COPY.muhurat_report.priceInr,
    headline: "Muhurat Report me personal timing paayein",
    subhead:
      "Purpose, kundali, dasha pressure, avoid windows, sankalp aur ritual checklist ko ek plan me paayein.",
  },
  [PLAN_ID.dosha_remedy_plan]: {
    priceInr: REPORT_COPY.dosha_remedy_plan.priceInr,
    headline: "Dosha Remedy Plan se right upay choose karein",
    subhead:
      "Dosha strength, cancellation, safe upay, mantra routine aur matched remedies ko chart se confirm karein.",
  },
};

export function reportOfferCopyFor(
  planId: SEOReportPlanId,
  language?: string | null
): ReportOfferCopy {
  const copyTable =
    normalizedReportCopyLanguage(language) === "hi" ? HINDI_REPORT_COPY : REPORT_COPY;
  return copyTable[planId] ?? REPORT_COPY.brihat_kundali;
}
